import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default:
                    "bg-primary text-primary-foreground shadow-[0_8px_24px_0_rgba(76,76,244,0.35)] hover:bg-primary/90",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                outline:
                    "border border-primary bg-background text-primary shadow-[0_8px_24px_0_rgba(76,76,244,0.15)] hover:bg-primary/5",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                footerLink: "text-slate-500 hover:text-white",
                socialIcon:
                    "h-10 w-10 rounded-2xl border border-white/10 bg-white/5 text-slate-100 hover:border-white/30 hover:bg-white/10 hover:text-white",
                white: "bg-white text-foreground shadow-none hover:bg-white/95",
                rose: "bg-brand-rose text-white shadow-[0_8px_24px_0_rgba(237,109,123,0.35)] hover:bg-brand-rose/90",
                menuSubLink:
                    "h-auto min-w-0 w-full justify-between gap-1 overflow-hidden rounded-lg px-2 py-2 text-sm font-normal text-muted-foreground hover:bg-background hover:text-foreground [&_svg]:size-3.5",
                menuCategoryTitle:
                    "h-auto min-w-0 w-full justify-start gap-3 overflow-hidden rounded-lg p-0 text-sm font-semibold text-foreground hover:bg-transparent hover:text-primary [&_svg]:size-5",
                menuCta:
                    "h-auto w-full justify-between rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/10",
                menuPanelAction:
                    "h-auto shrink-0 p-0 text-sm font-medium text-primary hover:bg-transparent hover:text-primary/80",
            },
            size: {
                default: "h-10 px-4 py-2",
                none: "h-auto p-0",
                sm: "h-9 rounded-lg px-3 ",
                lg: "h-11 rounded-2xl px-8 font-bold",
                xl: "h-14 rounded-2xl px-9 font-bold text-lg",
                icon: "h-10 w-10",
                logo: "w-auto h-7 px-2 rounded-xl",
            },
            palette: {
                indigo: "bg-indigo-600 text-white shadow-[0_10px_30px_-10px] shadow-indigo-600/55 hover:bg-indigo-600/90",
                emerald:
                    "bg-emerald-600 text-white shadow-[0_10px_30px_-10px] shadow-emerald-600/55 hover:bg-emerald-600/90",
                purple: "bg-purple-600 text-white shadow-[0_10px_30px_-10px] shadow-purple-600/55 hover:bg-purple-600/90",
                violet: "bg-violet-600 text-white shadow-[0_10px_30px_-10px] shadow-violet-600/55 hover:bg-violet-600/90",
                amber: "bg-amber-600 text-white shadow-[0_10px_30px_-10px] shadow-amber-600/55 hover:bg-amber-600/90",
                pink: "bg-pink-600 text-white shadow-[0_10px_30px_-10px] shadow-pink-600/55 hover:bg-pink-600/90",
                teal: "bg-teal-600 text-white shadow-[0_10px_30px_-10px] shadow-teal-600/55 hover:bg-teal-600/90",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, palette, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return <Comp className={cn(buttonVariants({ variant, size, palette, className }))} ref={ref} {...props} />;
    },
);
Button.displayName = "Button";

export { Button, buttonVariants };
